/**
 * Dev-only fixtures — V102-3-4 CMMC Scope stage. NEVER shipped
 * (loaded only under import.meta.env.DEV + VITE_FIXTURES).
 *
 * Four shapes keyed off `?scope=` (fixture-only query; URL dispatcher reads
 * it off window.location.search inside <ScopeStage>):
 *   empty      — 0 suggested, 0 in boundary → "Connect your first source" tile
 *   suggested  — 8 suggested, 0 in boundary → "8 waiting on you" + empty bnd
 *   mixed      — 3 suggested, 12 in boundary (default; richest state)
 *   done       — 0 suggested, 20 in boundary → "Scope complete" banner, CTA on
 *
 * Shapes conform to the server's AssetSuggestion[] and ScopedAsset[].
 */
import type {
  AssetSuggestion,
  AssetSuggestionSourceKind,
  ScopedAsset,
  Asset,
  AssetPeriodMembership,
  AssetTier,
} from '@aigovops/core';

export const SCOPE_STATES = ['empty', 'suggested', 'mixed', 'done'] as const;
export type ScopeFixtureState = (typeof SCOPE_STATES)[number];

export function scopeFixtureState(): ScopeFixtureState {
  try {
    const q = new URLSearchParams(window.location.search).get('scope');
    if (q && (SCOPE_STATES as readonly string[]).includes(q)) {
      sessionStorage.setItem('gp-fixtures-scope', q);
    }
    const s = q ?? sessionStorage.getItem('gp-fixtures-scope');
    return s && (SCOPE_STATES as readonly string[]).includes(s) ? (s as ScopeFixtureState) : 'mixed';
  } catch { return 'mixed'; }
}

const WS = 'ws-northwind';
const PROFILE = 'cert-cmmc';
const PERIOD = 'cmmc-fy26';
const AT = '2026-10-06T15:38:00.000Z';

const asset = (id: string, name: string, tier: AssetTier, over: Partial<Asset> = {}): Asset => ({
  asset_id: id, workspace_id: WS, system_id: null, name, tier,
  external_service_id: null, provider_key: null, discovered_from: 'manual',
  created_by: 'u-luis', created_at: AT, updated_at: AT, retired_at: null, retired_by: null, ...over,
});

const scoped = (a: Asset, category: string, rationale: string | null, treatment: ScopedAsset['treatment'], label: string): ScopedAsset => ({
  asset: a,
  membership: {
    membership_id: `apm-${a.asset_id}`, workspace_id: WS, profile_id: PROFILE, period_id: PERIOD,
    asset_id: a.asset_id, category, rationale,
    decided_by: 'u-dana', decided_at: AT, removed_at: null, removed_by: null, removed_reason: null,
  } satisfies AssetPeriodMembership,
  treatment, category_label: label,
});

const sg = (name: string, tier: AssetTier, source: AssetSuggestionSourceKind, label: string, id: string): AssetSuggestion => ({
  suggestion_key: `${source}:${id}`, name, tier, system_id: null, external_service_id: null,
  provider_key: null, discovered_from: source, source: { kind: source, id, label },
});

const SUGGESTED_SET = [
  sg('GitHub Enterprise — northwind-defense', 'middle_tier', 'connector', 'GitHub', 'c-gh'),
  sg('Entra ID (Northwind Defense tenant)', 'identity', 'connector', 'Entra ID', 'c-entra'),
  sg('AWS GovCloud VPC — build account', 'network', 'connector', 'AWS GovCloud', 'c-aws'),
  sg('CrowdStrike Falcon', 'endpoint_fleet', 'connector', 'CrowdStrike', 'c-cs'),
  sg('Splunk SIEM (gov)', 'other', 'connector', 'Splunk', 'c-splunk'),
  sg('OpenAI API — gpt-4o', 'ai_service', 'external_service', 'OpenAI', 'es-oai'),
  sg('Zendesk Support', 'other', 'discovered_tool', 'Zendesk', 'dt-zd'),
  sg('Rippling (HRIS)', 'other', 'external_service', 'Rippling', 'es-rp'),
];

const CUI = (ref: string) => ({ cat: 'cui', treatment: 'assessed' as const, label: 'CUI Asset', ref });
const SEC = (ref: string) => ({ cat: 'security_protection', treatment: 'assessed' as const, label: 'Security Protection Asset', ref });
const CRMA = (ref: string) => ({ cat: 'contractor_risk_managed', treatment: 'documented' as const, label: 'Contractor Risk Managed Asset', ref });
const SPC = (ref: string) => ({ cat: 'specialized', treatment: 'documented' as const, label: 'Specialized Asset', ref });
const OOS = (ref: string) => ({ cat: 'out_of_scope', treatment: 'excluded' as const, label: 'Out-of-Scope Asset', ref });

type CatSpec = { cat: string; treatment: ScopedAsset['treatment']; label: string; ref: string };
function s(a: Asset, cat: CatSpec, rationale: string | null): ScopedAsset {
  return scoped(a, cat.cat, rationale, cat.treatment, cat.label);
}

const BOUNDARY_MIXED: ScopedAsset[] = [
  s(asset('a-aws', 'AWS GovCloud VPC — build account', 'network'), CUI('a'), 'Build network for Halvorsen CUI source.'),
  s(asset('a-sp', 'Northwind CUI file share (SharePoint GCC-High)', 'database'), CUI('b'), 'Primary CUI document store.'),
  s(asset('a-cad', 'Halvorsen CAD workstations (12)', 'endpoint_fleet'), CUI('c'), 'Design workstations handling CUI drawings.'),
  s(asset('a-entra', 'Entra ID (Northwind Defense tenant)', 'identity'), CUI('d'), 'Authenticates every CUI-handling user.'),
  s(asset('a-eng', 'Engineering team (14 cleared)', 'other'), CUI('e'), 'Cleared engineers with CUI access.'),
  s(asset('a-cs', 'CrowdStrike Falcon', 'endpoint_fleet'), SEC('a'), 'EDR protecting CUI endpoints.'),
  s(asset('a-splunk', 'Splunk SIEM (gov)', 'other'), SEC('b'), 'Log aggregation for CUI assets.'),
  s(asset('a-mktg', 'Marketing laptop fleet', 'endpoint_fleet'), CRMA('a'), 'No CUI access by policy; EDR monitored.'),
  s(asset('a-oai', 'OpenAI API — gpt-4o (prod)', 'ai_service'), CRMA('b'), 'Policy-isolated; CUI prompts blocked.'),
  s(asset('a-cmm', 'CMM measurement machine', 'other'), SPC('a'), 'Firmware-locked OT; compensating controls documented.'),
  s(asset('a-hubspot', 'HubSpot CRM', 'other'), OOS('a'), 'Billing SaaS, no CUI touch.'),
  s(asset('a-hvac', 'Facilities HVAC console', 'other'), OOS('b'), 'Physically separated from CUI areas.'),
];

const BOUNDARY_DONE: ScopedAsset[] = [
  ...BOUNDARY_MIXED,
  s(asset('a-gh', 'GitHub Enterprise — northwind-defense org', 'middle_tier'), CUI('f'), 'Hosts CUI source + build artifacts.'),
  s(asset('a-ghrun', 'Build pipeline runners (GitHub self-hosted)', 'middle_tier'), CUI('g'), 'Compiles CUI source.'),
  s(asset('a-sql', 'CUI document SQL store', 'database'), CUI('h'), 'Structured CUI document index.'),
  s(asset('a-sentinel', 'Azure Sentinel', 'other'), SEC('c'), 'Secondary SIEM for GCC-High tenant.'),
  s(asset('a-rp', 'Rippling (HRIS)', 'other'), CRMA('c'), 'HRIS; no CUI; audited quarterly.'),
  s(asset('a-sales', 'Sales demo laptop', 'endpoint_fleet'), CRMA('d'), 'Policy-isolated; no CUI access.'),
  s(asset('a-rig', 'Acceptance test rig', 'other'), SPC('b'), 'Firmware-locked OT test equipment.'),
  s(asset('a-wifi', 'Facilities guest Wi-Fi', 'network'), OOS('c'), 'Isolated VLAN; no CUI reachability.'),
];

export interface ScopeFixturePayload {
  state: ScopeFixtureState;
  period_id: string;
  profile_id: string;
  suggestions: AssetSuggestion[];
  scope: ScopedAsset[];
  dismissed: Array<{ suggestion_key: string; name: string; reason: string; dismissed_by: string; dismissed_at: string }>;
  waiting_count: number;
  categorized_total: number;
  categorized_done: number;
  cta_enabled: boolean;
  banner_done: boolean;
  saved_at: string;
}

const DISMISSED_SAMPLE = [
  { suggestion_key: 'external_service:es-stripe', name: 'Stripe (billing)', reason: 'Billing SaaS, no CUI touch', dismissed_by: 'Dana', dismissed_at: '2026-10-04T18:12:00Z' },
  { suggestion_key: 'external_service:es-notion', name: 'Notion workspace', reason: 'Non-CUI knowledge base', dismissed_by: 'Luis', dismissed_at: '2026-10-02T14:03:00Z' },
];

export function scopeFixture(state: ScopeFixtureState = scopeFixtureState()): ScopeFixturePayload {
  switch (state) {
    case 'empty':
      return { state, period_id: PERIOD, profile_id: PROFILE, suggestions: [], scope: [], dismissed: [],
        waiting_count: 0, categorized_total: 0, categorized_done: 0, cta_enabled: false, banner_done: false, saved_at: '' };
    case 'suggested':
      return { state, period_id: PERIOD, profile_id: PROFILE, suggestions: SUGGESTED_SET, scope: [], dismissed: [],
        waiting_count: 8, categorized_total: 8, categorized_done: 0, cta_enabled: false, banner_done: false, saved_at: '2026-10-06T11:12:00Z' };
    case 'done':
      return { state, period_id: PERIOD, profile_id: PROFILE, suggestions: [], scope: BOUNDARY_DONE, dismissed: [],
        waiting_count: 0, categorized_total: BOUNDARY_DONE.length, categorized_done: BOUNDARY_DONE.length,
        cta_enabled: true, banner_done: true, saved_at: '2026-10-06T14:08:00Z' };
    case 'mixed':
    default:
      return { state: 'mixed', period_id: PERIOD, profile_id: PROFILE,
        suggestions: SUGGESTED_SET.slice(0, 3), scope: BOUNDARY_MIXED, dismissed: DISMISSED_SAMPLE,
        waiting_count: 3, categorized_total: 15, categorized_done: 12,
        cta_enabled: false, banner_done: false, saved_at: '2026-10-06T11:42:08Z' };
  }
}

